import { z } from 'zod'
import { objectToCamel } from 'ts-case-convert'
import type { ALL_GARMENTS } from '../../constants'
import * as DealerOrderSchemas from './order'
import * as ShipmentSchemas from './shipment' // avoid circular dependency
import { fabricSchema } from './fabric'
import { delayStatusSchema, orderStatusSchema } from './status'
import {
  garmentOptionPropertySchema,
  garmentOptionSchema,
  garmentOptionValuePropertySchema,
  garmentOptionValueSchema,
} from './option'
import { currencySchema } from './payment'

//* ---------------- GARMENT SUB RESOURCES ---------------- *//
//* These are sub resources that are different enough to    *//
//* not share from existing schemas                         *//

const baseGarmentFabricQuantitySchema = z.record(
  z.string(),
  z.object({
    width: z.string().nullable(),
    length: z.string().nullable(),
    is_cut: z.boolean().nullable().optional(),
    notes: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
  }),
)

const baseGarmentButtonQuantitySchema = z.record(
  z.string(),
  z.record(
    z.string(),
    z.object({
      quantity: z.coerce.number(),
      options: z.array(z.string()).nullable(),
    }),
  ),
)

const garmentBaseMaterialSchema = z.object({
  id: z.union([z.number(), z.string()]).nullable(),
  name: z.string(),
  description: z.string().nullable(),
  image: z.string().nullable(),
  options: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      description: z.string(),
      garment_type: z.number(),
      garment_types: z.array(z.string()),
    }),
  ),
})
export const garmentButtonSchema = garmentBaseMaterialSchema.extend({
  quantity: baseGarmentButtonQuantitySchema,
})
export const garmentFabricSchema = garmentBaseMaterialSchema.extend({
  usage: z.string().nullable(),
  trinity_number: z.string(),
  supplier_number: z.string().nullable(),
  quantity: baseGarmentFabricQuantitySchema,
})
export const garmentFeltSchema = garmentBaseMaterialSchema.extend({})
export const garmentLabelSchema = garmentBaseMaterialSchema.extend({})
export const garmentRibknitSchema = garmentBaseMaterialSchema.extend({})
export const garmentSuedeSchema = garmentBaseMaterialSchema.extend({})
export const garmentThreadSchema = garmentBaseMaterialSchema.extend({})
export const garmentZipperSchema = garmentBaseMaterialSchema.extend({})

export const garmentMaterialsSchema = z
  .object({
    buttons: z.array(garmentButtonSchema),
    dealer_labels: z.array(garmentLabelSchema),
    fabrics: z.array(garmentFabricSchema),
    felts: z.array(garmentFeltSchema),
    labels: z.array(garmentLabelSchema),
    leather_patches: z.array(garmentBaseMaterialSchema),
    mill_labels: z.array(garmentLabelSchema),
    monograms: z.array(garmentBaseMaterialSchema),
    pocket_bags: z.array(garmentBaseMaterialSchema),
    ribknits: z.array(garmentRibknitSchema),
    suedes: z.array(garmentSuedeSchema),
    threads: z.array(garmentThreadSchema),
    waistbands: z.array(garmentBaseMaterialSchema),
    zippers: z.array(garmentZipperSchema),
  })
  .transform(objectToCamel)

//* ---------------- BASE ---------------- *//
const sharedGarmentSchema = z.object({
  id: z.number(),
  title: z.string(),
  order_id: z.number(),
  copied_garment_id: z.number().nullable(),
  price: z.string(),
  option_cost: z.string(),
  garment_type: z.string(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date().nullable(),
})

const baseGarmentSchema = sharedGarmentSchema.extend({
  order_status_id: z.number(),
  delay_status_id: z.number(),
  fabric_url: z.string().url().nullable(),
  shipping_class: z.union([z.literal('direct'), z.literal('bulk')]),
  dealer_hold: z.string().nullable(),
  overnight_ship: z.boolean().optional(),
  order_status: orderStatusSchema.nullable().optional(),
  delay_status: delayStatusSchema.nullable().optional(),
  dealer_order: z.lazy(() => DealerOrderSchemas.dealerOrderSchema).optional(),
  shipments: z.array(z.lazy(() => ShipmentSchemas.shipmentSchema)).optional(),
})

export const baseGarmentHomeSchema = sharedGarmentSchema.extend({
  status_updated_at: z.coerce.date(),
})

const baseGarmentPropertiesSchema = z.object({
  models: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      pattern: z.string(),
      block: z.string(),
      garment_type: z.string(),
      size: z.number(),
    }),
  ),
  measurements: z.array(
    z.object({
      id: z.number().nullable(),
      name: z.string(),
      description: z.string(),
      value: z.union([z.coerce.number(), z.string()]),
      last_modified: z.coerce.date(),
      garment_type: z.union([z.number(), z.string()]),
      garment_types: z.array(z.string()),
      units: z.string().optional(),
    }),
  ),
  options: z.array(
    z.object({
      option: garmentOptionPropertySchema,
      option_value: garmentOptionValuePropertySchema,
      garment_type: z.number(),
      garment_types: z.array(z.string()),
    }),
  ),
  special_instructions: z.array(z.string()),
  materials: garmentMaterialsSchema,
})

//* ---------------- SCHEMAS ---------------- *//
export const garmentSchema = baseGarmentSchema.transform(objectToCamel)

export const garmentExtendedSchema = baseGarmentSchema
  .extend({
    rendered_uri: z.string().url().nullable().optional(),
    prefix: z.string().nullable(),
    index: z.string().nullable(),
    last_status_change_date: z.coerce.date().nullable(),
    last_delay_change_date: z.coerce.date().nullable(),
    manufacturer: z.object({
      id: z.number(),
      name: z.string(),
      email: z.string().nullable(),
    }),
    currency: currencySchema.nullable().optional(),
    fabric: fabricSchema,
    // override  the following to be required in extended schema
    dealer_order: z.lazy(() => DealerOrderSchemas.dealerOrderSchema),
    order_status: orderStatusSchema.nullable(),
    delay_status: delayStatusSchema.nullable(),
    shipments: z.array(z.lazy(() => ShipmentSchemas.shipmentSchema)),
  })
  .transform(objectToCamel)

export const garmentHomeSchema = baseGarmentHomeSchema.transform(objectToCamel)

export const baseModelSchema = z
  .object({
    id: z.number(),
    model: z.string(),
    description: z.string(),
    image: z.string(),
    garment_type: z.number(),
    tailoring_grade: z.number(),
    order_type: z.number(),
    active: z.boolean(),
    has_image: z.boolean(),
    display_order: z.number(),
  })
  .transform(objectToCamel)

export const customModelSchema = z
  .object({
    id: z.number(),
    model_desc: z.string(),
    active: z.boolean(),
    is_parent: z.boolean(),
    blocks: z.array(z.object({ id: z.number(), description: z.string() })),
    base_model: baseModelSchema,
  })
  .transform(({ model_desc, ...data }) => ({ description: model_desc, ...data }))
  .transform(objectToCamel)

export const garmentTypeSchema = z.number()

export const garmentPropertiesSchema = baseGarmentPropertiesSchema.transform(objectToCamel)

export const garmentHistorySchema = z
  .object({
    id: z.number(),
    garment_id: z.number(),
    user_id: z.number().nullable(),
    username: z.string().nullable(),
    status_type: z.string(),
    status_id: z.number(),
    status_code: z.string(),
    notes: z.string().nullable(),
    timestamp: z.coerce.date(),
  })
  .transform(objectToCamel)

export const garmentSuitOptionSchema = z
  .object({
    updated: z.coerce.boolean(),
    updated_at: z.coerce.date(),
    text: z.string().nullable(),
    option: garmentOptionSchema,
    option_value: garmentOptionValueSchema,
  })
  .transform(objectToCamel)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type GarmentType = z.infer<typeof garmentSchema>
    type GarmentExtendedType = z.infer<typeof garmentExtendedSchema>
    type GarmentHomeType = z.infer<typeof garmentHomeSchema>
    type BaseModelType = z.infer<typeof baseModelSchema>
    type GarmentTypeType = z.infer<typeof garmentTypeSchema>
    type GarmentTypesType = (typeof ALL_GARMENTS)[number]
    type CustomModelType = z.infer<typeof customModelSchema>
    type GarmentPropertiesType = z.infer<typeof garmentPropertiesSchema>
    type GarmentMaterialsType = z.infer<typeof garmentMaterialsSchema>
    type GarmentHistoryType = z.infer<typeof garmentHistorySchema>
    type GarmentSuitOptionType = z.infer<typeof garmentSuitOptionSchema>
  }
}

//* ---------------- HELPERS ---------------- *//
